/**
 * CUSTOMER COSTS - Per-customer cost breakdown
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import CostChart from '../components/admin/financials/CostChart';
import MetricCard from '../components/admin/financials/MetricCard';
import InvoiceGenerator from '../components/admin/financials/InvoiceGenerator';
import apiClient from '../lib/apiClient';
import { AlertCircle, RefreshCw } from 'lucide-react';

export default function CustomerCosts() {
  const { tenant_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [customerData, setCustomerData] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (tenant_id) {
      loadCustomerData(tenant_id);
    } else {
      // Load all customers list
      loadAllCustomers();
    }
  }, [tenant_id]);

  const loadCustomerData = async (tenantId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/admin/financials/customers/${tenantId}`);
      setCustomerData(response.data);
      console.log('✅ Customer data loaded:', response.data);
    } catch (err) {
      console.error('❌ Error loading customer data:', err);
      setError(err.message || 'Failed to load customer data');
      // Set empty data structure to prevent blank page
      setCustomerData({
        customer: { name: 'Unknown', email: 'N/A' },
        total_cost: 0,
        total_events: 0,
        breakdown_by_provider: {},
        breakdown_by_event_type: {},
        cost_trend: [],
        unit_economics: {},
        recent_invoices: []
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAllCustomers = async () => {
    // For now, show message to select a customer
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!tenant_id) {
    return (
      <div className="p-8 bg-gray-50 min-h-screen">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Customer Costs</h1>
          <p className="text-gray-600 mb-8">Select a customer from the dashboard to view their cost details</p>
          <a href="/admin/financials" className="px-6 py-3 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700">
            Go to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-900">Failed to load customer data</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <button
              onClick={() => loadCustomerData(tenant_id)}
              className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{customerData?.customer?.name || 'Customer'}</h1>
        <p className="text-gray-600 mt-2">{customerData?.customer?.email}</p>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <MetricCard
          label="Total Cost"
          value={customerData?.total_cost || 0}
          format="currency"
        />
        <MetricCard
          label="Total Events"
          value={customerData?.total_events || 0}
          format="number"
        />
        <MetricCard
          label="Avg Cost per Event"
          value={(customerData?.total_cost || 0) / (customerData?.total_events || 1)}
          format="currency"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost by Provider</h3>
          <CostChart
            type="pie"
            data={Object.entries(customerData?.breakdown_by_provider || {}).map(([name, value]) => ({
              name,
              value
            }))}
            config={{ nameKey: 'name', valueKey: 'value', height: 300 }}
          />
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost by Event Type</h3>
          <CostChart
            type="pie"
            data={Object.entries(customerData?.breakdown_by_event_type || {}).map(([name, value]) => ({
              name: name.replace(/_/g, ' '),
              value
            }))}
            config={{ nameKey: 'name', valueKey: 'value', height: 300 }}
          />
        </div>
      </div>

      {/* Cost Trend */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Trend</h3>
        <CostChart
          type="line"
          data={customerData?.cost_trend || []}
          config={{ xKey: 'date', yKey: 'cost', height: 300 }}
        />
      </div>

      {/* Unit Economics */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Unit Economics</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-600 mb-1">Cost per Message</p>
            <p className="text-2xl font-bold text-gray-900">
              ${parseFloat(customerData?.unit_economics?.cost_per_message || 0).toFixed(4)}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-600 mb-1">Cost per Conversation</p>
            <p className="text-2xl font-bold text-gray-900">
              ${parseFloat(customerData?.unit_economics?.cost_per_conversation || 0).toFixed(2)}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-600 mb-1">Total Messages</p>
            <p className="text-2xl font-bold text-gray-900">
              {customerData?.unit_economics?.total_messages || 0}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-600 mb-1">Total Conversations</p>
            <p className="text-2xl font-bold text-gray-900">
              {customerData?.unit_economics?.total_conversations || 0}
            </p>
          </div>
        </div>
      </div>

      {/* Recent Invoices */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Invoices</h3>
        {customerData?.recent_invoices && customerData.recent_invoices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {customerData.recent_invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{invoice.invoice_number}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{new Date(invoice.invoice_date).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">${parseFloat(invoice.total).toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                        invoice.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                        invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {invoice.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No invoices yet</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-4">
        <button
          onClick={() => setShowInvoiceModal(true)}
          className="px-6 py-3 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700"
        >
          Generate Invoice
        </button>
      </div>

      {/* Invoice Generator Modal */}
      <InvoiceGenerator
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        onGenerated={() => {
          setShowInvoiceModal(false);
          loadCustomerData(tenant_id);
        }}
        prefilledTenantId={tenant_id}
      />
    </div>
  );
}
